/**
 * AuthBootstrap — wraps the app, calls GET /api/auth/me once on mount
 * to hydrate the store with the live session state. Until /me resolves
 * the store stays `loading` (or rehydrated from persisted state, if
 * any). After /me lands the store flips to `authenticated` (200) or
 * `unauthenticated` (401/403/network).
 *
 * The component renders its children unconditionally — route guards
 * read `useAuthStore().status` to decide whether to redirect.
 */
import { type ReactNode, useEffect } from 'react';
import { useAuthStore } from '../stores/auth-store';
import { ApiError, authApi } from './api';

export function AuthBootstrap({ children }: { children: ReactNode }) {
  const setLoading = useAuthStore((s) => s.setLoading);
  const setSession = useAuthStore((s) => s.setSession);
  const setUnauthenticated = useAuthStore((s) => s.setUnauthenticated);

  useEffect(() => {
    let cancelled = false;
    setLoading();
    (async () => {
      try {
        const me = await authApi.me();
        if (!cancelled) setSession(me);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          setUnauthenticated();
        } else {
          // Treat network/server failure as "unauthenticated" so the UI
          // shows the login page; deeper diagnostics come from OTel.
          setUnauthenticated();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setLoading, setSession, setUnauthenticated]);

  return <>{children}</>;
}
